import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject, catchError, of, switchMap, takeUntil, tap } from "rxjs";
import { environment } from "../../environments/environment";

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private readonly apiUrl = `${environment.apiUrl}`;
    private destroy$ = new Subject<void>();

    private userSubject = new BehaviorSubject<User | null>(null);
    user$ = this.userSubject.asObservable();

    constructor(private http: HttpClient) {
    }

    init() {
        this.tryGetUser().pipe(
            takeUntil(this.destroy$)
        ).subscribe();
    }

    login(email: string, password: string) {
        return this.http.post<Response>(`${this.apiUrl}/login/?useCookies=true`, {
            email: email,
            password: password
        });
    }

    register(request: RegisterRequest) {
        return this.http.post<Response>(`${this.apiUrl}/register`, request
        ).pipe(switchMap(() => this.login(request.email, request.password)));
    }

    logout() {
        return this.http.post<Response>(`${this.apiUrl}/logout`, null,
        ).pipe(tap(() => this.userSubject.next(null)));
    }

    sendNewPassword(email: string) {
        const request = {
            email: email
        };

        return this.http.post<Response>(`${this.apiUrl}/forgotPassword`, request
        );
    }

    tryGetUser() {
        return this.isLogged().pipe(
            switchMap(isLogged => {
                if (isLogged) {
                    return this.getUser();
                }
                else {
                    return of(null);
                }
            }),
            catchError(() => of(null))
        );
    }

    updateUser(request: UpdateUserRequest) {
        return this.http.post<User>(`${this.apiUrl}/manage/info`, request).pipe(
            tap(user => {
                this.userSubject.next(user);
            }));
    }

    deleteAccount() {
        return this.http.post(`${this.apiUrl}/manage/deleteAccount`, null).pipe(
            tap(() => {
                this.userSubject.next(null);
            }));
    }

    private isLogged(): Observable<boolean> {
        return this.http.get<boolean>(`${this.apiUrl}/isLogged`);
    }

    loginWithOAuth(provider: 'google' | 'facebook' | 'apple') {
        const returnUrl = encodeURIComponent(window.location.origin + window.location.pathname);
        window.location.href = `${this.apiUrl}/auth/${provider}?returnUrl=${returnUrl}`;
    }

    handleOAuthCallback() {
        const urlParams = new URLSearchParams(window.location.search);
        const isExternalLogin = urlParams.get('externalLogin');
        const error = urlParams.get('error');

        if (isExternalLogin === '1') {
            window.history.replaceState({}, document.title, window.location.pathname);

            this.tryGetUser().pipe(
                takeUntil(this.destroy$)
            ).subscribe();

            return true;
        } else if (isExternalLogin === '0' || error) {
            window.history.replaceState({}, document.title, window.location.pathname);

            console.error('OAuth authentication failed:', error || 'Unknown error');

            return true;
        }

        return false;
    }

    private getUser(): Observable<User> {
        return this.http.get<User>(`${this.apiUrl}/manage/info`).pipe(
            tap(user => {
                this.userSubject.next(user);
            }));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

export interface RegisterRequest {
    firstName: string;
    email: string;
    password: string;
    newsletterAccepted: boolean;
}

export interface User {
    firstName: string;
    email: string;
    isEmailConfirmed: boolean;
    newsletterAccepted: boolean;
    lastName?: string;
    street?: string;
    postalCode?: string;
    city?: string;
}

export interface UpdateUserRequest {
    firstName: string;
    lastName?: string;
    street?: string;
    postalCode?: string;
    city?: string;
    oldPassword?: string;
    newPassword?: string;
}

export interface Response {
    isSuccess: boolean;
    message: string;
}
